/**
 * Widget types that belong to the automations & efficiency section.
 * The automations footer is hidden for these widgets since they already display automation-related content.
 */
export const AUTOMATIONS_SECTION_WIDGET_TYPES = [
  'most-popular-automations',
  'automation-enrolments',
  'time-saved',
  'custom-automations',
  'actions-this-period',
  'recent-automation-enrollments',
  'manual-vs-automated'
]

/**
 * Maps widget types to their associated best practice API types.
 * A widget may link to one or more best practice types.
 */
export const WIDGET_TO_BEST_PRACTICE_TYPES = {
  'upcoming-tours': ['TOUR_REMINDER'],
  'completed-tours': ['TOUR_FOLLOW_UP'],
  'converted-leads-from-tours': ['TOUR_FOLLOW_UP'],
  'conversion-rate-from-tours': ['TOUR_FOLLOW_UP'],
  'tour-pass-plan-purchase': ['TOUR_FOLLOW_UP'],
  'checked-in': ['INACTIVITY_FOLLOW_UP'],
  'check-ins': ['INACTIVITY_FOLLOW_UP'],
  'tasks': ['OPERATIONAL_TASKS'],
  'upcoming-bookings': ['REPEAT_BOOKER_FEEDBACK'],
  'top-bookers': ['REPEAT_BOOKER_FEEDBACK'],
  'assignments-starting-soon': ['ASSIGNMENT_ONBOARDING'],
  'assignments-ending-soon': ['ASSIGNMENT_RENEWAL'],
  'latest-messages': ['ROUTINE_COMMUNICATION'],
  'invoices': ['INVOICE_REMINDER', 'INVOICE_OVERDUE_FOLLOW_UP'],
  'invoices-paid': ['INVOICE_REMINDER'],
  'unpaid-invoices': ['INVOICE_OVERDUE_FOLLOW_UP'],
  'converted-leads-from-inquiries': ['INQUIRY_FOLLOW_UP'],
  'conversion-rate-from-inquiries': ['INQUIRY_FOLLOW_UP'],
  'converted-leads-from-dropins': ['DROP_IN_FOLLOW_UP'],
  'conversion-rate-from-dropins': ['DROP_IN_FOLLOW_UP'],
  'dropin-repeat-booking': ['DROP_IN_FOLLOW_UP'],
  'dropin-pass-plan-purchase': ['DROP_IN_FOLLOW_UP'],
  'plans-starting-soon': ['PLAN_ONBOARDING'],
  'plans-ending-soon': ['PLAN_RENEWAL']
}

/**
 * Copy text for each best practice type, used in the footer and detail panel.
 * {N} is replaced with proof_count (covered) or opportunity_count (missed opportunity).
 */
export const BEST_PRACTICE_COPY = {
  TOUR_REMINDER: {
    label: 'Tour reminders',
    description: 'Send every tour guest a reminder before they arrive so they show up informed, confident, and ready to convert.',
    covered: '{N} tour {reminder|reminders} sent automatically',
    coveredDetailed: 'tour {reminder|reminders} sent automatically',
    coveredZero: 'Tour reminders are automated',
    missedShort: '{N} {tour|tours} booked \u2013 automate reminders',
    missedDetailed: '{tour|tours} booked, but no automated reminders. Reduce no-shows by automating this.',
    noCoverage: 'Reduce no-shows by automating reminders'
  },
  TOUR_FOLLOW_UP: {
    label: 'Tour follow-ups',
    description: 'Follow up with every tour guest after their visit while the experience is still fresh \u2014 to answer questions, remove doubt, and move them toward membership.',
    covered: '{N} tour {follow-up was|follow-ups were} automated',
    coveredDetailed: 'tour {follow-up was|follow-ups were} automated',
    coveredZero: 'Tour follow-ups are automated',
    missedShort: '{N} {tour|tours} booked \u2013 automate follow-ups',
    missedDetailed: '{tour|tours} booked, but no automated follow-ups. Capture more conversions by automating this.',
    noCoverage: 'Automate follow-ups to convert more leads'
  },
  INACTIVITY_FOLLOW_UP: {
    label: 'Inactivity follow-ups',
    description: 'Detect when users haven\u2019t checked in for a while and follow up automatically before absence becomes a decision to leave.',
    covered: '{N} inactive {user|users} automatically followed up',
    coveredDetailed: 'inactive {user|users} automatically followed up',
    coveredZero: 'Inactivity follow-ups are automated',
    missedShort: '{N} {user hasn\u2019t|users haven\u2019t} been back \u2014 automate outreach',
    missedDetailed: 'active {user hasn\u2019t|users haven\u2019t} checked in more than 30 days after their previous check-in. Automate follow-ups to prevent silent churn.',
    noCoverage: 'Automate follow-ups to prevent silent churn',
    maxCount: 20
  },
  OPERATIONAL_TASKS: {
    label: 'Operational tasks',
    description: 'Routine operational tasks should be system-generated, not manually remembered.',
    covered: '{N} {task|tasks} created by automations',
    coveredDetailed: '{task|tasks} created by automations',
    coveredZero: 'Task creation is automated',
    missedShort: 'Automate routine follow-ups to stay ahead',
    missedDetailed: '{task|tasks} created manually. Automate routine follow-ups from inquiries, bookings, and renewals to ensure nothing is missed and your process runs consistently.',
    noCoverage: 'Automate routine follow-ups to stay ahead'
  },
  REPEAT_BOOKER_FEEDBACK: {
    label: 'Repeat booker feedback',
    description: 'Collect feedback from repeat bookers to strengthen retention and improve experience.',
    covered: '{N} repeat-booker feedback {request|requests} automated',
    coveredDetailed: 'repeat-booker feedback {request|requests} automated',
    coveredZero: 'Feedback requests are automated',
    missedShort: '{N} {user|users} made repeat bookings \u2013 automate feedback',
    missedDetailed: '{user has|users have} booked 5+ times. Automate feedback requests to improve experience.',
    noCoverage: 'Collect feedback from repeat bookers'
  },
  ASSIGNMENT_ONBOARDING: {
    label: 'Assignment onboarding',
    description: 'Ensure new assignments start smoothly with pre-start communication and internal preparation.',
    covered: '{N} assignment starting soon {reminder|reminders} automated',
    coveredDetailed: 'assignment starting soon {reminder|reminders} automated',
    coveredZero: 'Assignment onboarding is automated',
    missedShort: '{N} {assignment|assignments} starting soon \u2013 automate onboarding',
    missedDetailed: '{N} {assignment starts|assignments start} in the next 7 days. Automate pre-start communication to ensure a smooth first day.',
    noCoverage: 'Automate onboarding before assignments begin'
  },
  ASSIGNMENT_RENEWAL: {
    label: 'Assignment renewal',
    description: 'Reach out to every member before their assignment ends \u2014 with enough lead time to open a renewal conversation, not just react to a departure.',
    covered: '{N} renewal {reminder|reminders} sent automatically',
    coveredDetailed: 'renewal {reminder|reminders} sent automatically',
    coveredZero: 'Renewal reminders are automated',
    missedShort: '{N} {assignment|assignments} ending soon \u2013 automate outreach',
    missedDetailed: '{N} {assignment ends|assignments end} in the next 30 days. Automate renewal outreach so users are contacted before they quietly leave.',
    noCoverage: 'Automate renewal outreach before assignments end'
  },
  ROUTINE_COMMUNICATION: {
    label: 'Routine communication',
    description: 'Routine member communications \u2014 reminders, confirmations, follow-ups \u2014 should run automatically, so operators can focus their attention on conversations that require a human.',
    covered: '{N} routine {message|messages} sent automatically',
    coveredDetailed: 'routine {message|messages} sent automatically',
    coveredZero: 'Routine messages are automated',
    missedShort: 'Routine messages are being sent manually \u2013 automate them',
    missedDetailed: 'routine {message|messages} being sent manually. Automate confirmations, reminders, follow-ups, and re-engagement so users get consistent, timely communication.',
    noCoverage: 'Routine messages are being sent manually \u2013 automate them'
  },
  INVOICE_REMINDER: {
    label: 'Invoice reminders',
    description: 'Automate invoice reminders before and on due dates so that payment collection runs consistently without manual chasing.',
    covered: '{N} invoice {reminder|reminders} sent automatically',
    coveredDetailed: 'invoice {reminder|reminders} sent automatically',
    coveredZero: 'Invoice reminders are automated',
    missedShort: '{N} due \u2013 no automated reminders running',
    missedDetailed: 'due {invoice|invoices}, but no automated reminders. Get paid more reliably by sending reminders before the due date.',
    noCoverage: 'Automate payment reminders before due dates'
  },
  INVOICE_OVERDUE_FOLLOW_UP: {
    label: 'Overdue invoice follow-ups',
    description: 'Follow up automatically on every overdue invoice so no payment slips through the cracks without a prompt.',
    covered: '{N} overdue {follow-up was|follow-ups were} automated',
    coveredDetailed: 'overdue {follow-up was|follow-ups were} automated',
    coveredZero: 'Overdue follow-ups are automated',
    missedShort: '{N} overdue {invoice|invoices} \u2013 automate follow-ups',
    missedDetailed: 'overdue {invoice|invoices}, but no automated follow-ups. Reduce chasing by automating this.',
    noCoverage: 'Automate overdue invoice follow-ups'
  },
  INQUIRY_FOLLOW_UP: {
    label: 'Inquiry follow-ups',
    description: 'Respond immediately to every inquiry automatically and follow up consistently until the prospect converts, books a tour, or explicitly disengages.',
    covered: '{N} inquiry {follow-up|follow-ups} sent automatically',
    coveredDetailed: 'inquiry {follow-up|follow-ups} sent automatically',
    coveredZero: 'Inquiry follow-ups are automated',
    missedShort: '{N} {inquiry|inquiries} received \u2013 automate follow-ups',
    missedDetailed: '{inquiry|inquiries} received but follow-up is manual. The fastest spaces to respond win the most members. Automate your follow-up sequence so no lead goes cold.',
    noCoverage: 'Automate follow-ups to convert inquiries'
  },
  DROP_IN_FOLLOW_UP: {
    label: 'Drop-in follow-ups',
    description: 'Follow up with every drop-in visitor automatically while the experience is still fresh.',
    covered: '{N} drop-in {follow-up|follow-ups} ran automatically',
    coveredDetailed: 'drop-in {follow-up|follow-ups} ran automatically',
    coveredZero: 'Drop-in follow-ups are automated',
    missedShort: '{N} drop-in {booking|bookings} made \u2013 automate follow-ups',
    missedDetailed: 'drop-in {visitor has|visitors have} already experienced your space. A same-day follow-up is a great conversion lever that you don\u2019t have automated.',
    noCoverage: 'Automate follow-ups to convert drop-ins'
  },
  PLAN_ONBOARDING: {
    label: 'Plan onboarding',
    description: 'Ensure every new plan member receives a welcome communication before their plan begins \u2014 so their first experience is one of feeling prepared and expected, not dropped into the space cold.',
    covered: '{N} plan onboarding {message|messages} sent automatically',
    coveredDetailed: 'plan onboarding {message|messages} sent automatically',
    coveredZero: 'Plan onboarding is automated',
    missedShort: '{N} {plan starts|plans start} soon \u2013 no welcome message scheduled',
    missedDetailed: '{plan is|plans are} starting soon with no welcome communication scheduled. Automate onboarding so new users feel prepared and expected from day one.',
    noCoverage: 'Automate onboarding for strong starts to plans'
  },
  PLAN_RENEWAL: {
    label: 'Plan renewal',
    description: 'Reach out to every member before their plan ends \u2014 with enough lead time to have a real renewal conversation, not a last-minute scramble.',
    covered: '{N} plan-ending-soon {reminder|reminders} sent automatically',
    coveredDetailed: 'plan-ending-soon {reminder|reminders} sent automatically',
    coveredZero: 'Plan renewal reminders are automated',
    missedShort: '{N} {plan has|plans have} end dates \u2013 no renewal outreach automated yet',
    missedDetailed: '{plan|plans} set to end with no renewal outreach scheduled. Automate renewal messages so users are asked to stay.',
    noCoverage: 'Automate renewal outreach to retain customers'
  }
}

/**
 * Get the list of best practice API types for a widget.
 * @param {string} widgetType - Widget type (e.g. 'upcoming-tours')
 * @returns {string[]}
 */
export function getWidgetBestPracticeTypes(widgetType) {
  return WIDGET_TO_BEST_PRACTICE_TYPES[widgetType] || []
}

/**
 * Format copy text by replacing {N} placeholder with the given count.
 * When maxCount is provided, counts above it display as "maxCount+".
 * @param {string} template - Text template with {N} placeholder
 * @param {number} count - Count to insert
 * @param {number} [maxCount] - Optional cap (e.g. 20 → shows "20+" for values above)
 * @returns {string}
 */
export function formatBestPracticeCopy(template, count, maxCount) {
  if (!template) return ''
  const n = count ?? 0
  const display = (maxCount != null && n > maxCount) ? `${maxCount}+` : String(n)
  let result = template.replace(/\{N\}/g, display)
  // Handle plural markers: {singular|plural} picks based on count
  result = result.replace(/\{([^|}]+)\|([^}]+)\}/g, (_match, singular, plural) => {
    return n === 1 ? singular : plural
  })
  return result
}

const INSIGHT_DISMISSED_KEY = 'dashboard-automation-insight-dismissed'

/**
 * Mark the insight as dismissed for a widget type.
 * @param {string} widgetType - Widget type (e.g. 'upcoming-tours')
 */
export function dismissInsight(widgetType) {
  try {
    const stored = localStorage.getItem(INSIGHT_DISMISSED_KEY)
    const dismissed = stored ? JSON.parse(stored) : {}
    dismissed[widgetType] = true
    localStorage.setItem(INSIGHT_DISMISSED_KEY, JSON.stringify(dismissed))
  } catch (e) {
    console.error('Failed to save insight dismiss:', e)
  }
}

/**
 * Whether the insight was dismissed for a widget type.
 * @param {string} widgetType - Widget type (e.g. 'upcoming-tours')
 * @returns {boolean}
 */
export function isInsightDismissed(widgetType) {
  try {
    const stored = localStorage.getItem(INSIGHT_DISMISSED_KEY)
    const dismissed = stored ? JSON.parse(stored) : {}
    return !!dismissed[widgetType]
  } catch {
    return false
  }
}

/**
 * Clear dismissed state on page load (for testing).
 */
export function clearInsightDismissedOnLoad() {
  try {
    localStorage.removeItem(INSIGHT_DISMISSED_KEY)
  } catch (_e) {
    // Ignore localStorage errors
  }
}

/** Map widget titles to widget types for fallback when widgetType is missing from URL */
const TITLE_TO_WIDGET_TYPE = {
  'Plans ending soon': 'plans-ending-soon',
  'Plans starting soon': 'plans-starting-soon',
  'Upcoming tours': 'upcoming-tours',
  'Completed tours': 'completed-tours',
  'Upcoming bookings': 'upcoming-bookings',
  'Assignments ending soon': 'assignments-ending-soon',
  'Assignments starting soon': 'assignments-starting-soon',
  'Currently checked in': 'checked-in',
  'Previous check-ins': 'check-ins',
  'Tasks': 'tasks',
  'Your tasks': 'tasks',
  'Top bookers': 'top-bookers',
  'Time Saved': 'time-saved',
  'Custom Automations': 'custom-automations',
  'Tasks handled automatically': 'actions-this-period',
  'Recent Automation Enrollments': 'recent-automation-enrollments',
  'Inbox': 'latest-messages',
  'Best practices automated': 'most-popular-automations',
  'Most enrolled automations': 'automation-enrolments',
  'Revenue collected': 'invoices-paid',
  'Unpaid invoices': 'unpaid-invoices',
  'Invoices': 'invoices',
  'Plan revenue change': 'plan-revenue',
  'Top accounts by revenue': 'top-accounts-by-revenue',
  'Manual vs. automated actions': 'manual-vs-automated',
  'Tour \u2192 member conversions': 'converted-leads-from-tours',
  'Tour \u2192 members': 'converted-leads-from-tours',
  'Converted leads from Tours': 'converted-leads-from-tours',
  'Tour \u2192 member conversions rate': 'conversion-rate-from-tours',
  'Conversion rate from Tours': 'conversion-rate-from-tours',
  'Inquiry \u2192 member conversions': 'converted-leads-from-inquiries',
  'Inquiry \u2192 members': 'converted-leads-from-inquiries',
  'Converted leads from Inquiries': 'converted-leads-from-inquiries',
  'Inquiry \u2192 member conversions rate': 'conversion-rate-from-inquiries',
  'Conversion rate from Inquiries': 'conversion-rate-from-inquiries',
  'Drop-in \u2192 member conversions': 'converted-leads-from-dropins',
  'Drop-in \u2192 members': 'converted-leads-from-dropins',
  'Converted leads from Drop-ins': 'converted-leads-from-dropins',
  'Drop-in \u2192 member conversions rate': 'conversion-rate-from-dropins',
  'Conversion rate from Drop-ins': 'conversion-rate-from-dropins',
  'Tour \u2192 plan/pass purchase': 'tour-pass-plan-purchase',
  'Drop-in \u2192 return booking': 'dropin-repeat-booking',
  'Drop-in \u2192 plan/pass purchase': 'dropin-pass-plan-purchase'
}

/**
 * Infer widget type from title when widgetType is not in URL.
 * @param {string} title - Widget title (e.g. 'Plans ending soon')
 * @returns {string|null}
 */
export function getWidgetTypeFromTitle(title) {
  if (!title) return null
  const normalized = title.trim()
  return TITLE_TO_WIDGET_TYPE[normalized] || null
}
